/**
 * PD-1523 — the data behind the printable "Patient Demographic & Contact
 * Information" sheet (the form Alina asked for). Kept as a pure mapping so the
 * pdfmake layer in `demographicsPdf.ts` stays layout-only and this can be tested
 * without rendering a PDF.
 *
 * An empty string means "beHuman does not hold this value". Per Alina, the sheet
 * prints only what we actually have — the renderer drops every empty field, and
 * a section left with nothing disappears with it. Fields the product never
 * captures at all (practice name) are therefore absent from this model rather
 * than mapped to a permanent blank.
 *
 * The provider is the one the patient was last SEEN by (Alina: "the provider that
 * they saw"), resolved by middleware from their appointments — not the PCP.
 */
import type { Coverage, HumanName, Patient } from '@medplum/fhirtypes'
import { formatHumanName } from '@medplum/core'
import { formatPhone } from '@/utils.ts'
import {
  formatPharmacyAddress,
  readDefaultPharmacy,
} from '@/pages/Patient/components/DefaultPharmacy/defaultPharmacy'
import {
  convertFromISODate,
  getInsuranceDisplayData,
  getSubscriberDob,
  getSubscriberName,
} from '@/utils/coverageUtils'

/**
 * The provider block exactly as middleware's `getLastSeenProvider` returns it —
 * the provider the patient was last actually seen by. Flat by design: the NPI,
 * phone and fax come off a Practitioner the web must not read itself.
 */
export type SheetProviderInput = { name: string; npi: string; phone: string; fax: string }

/** The three boxes on the form's Sex line; '' checks none of them. */
export type SheetSex = 'male' | 'female' | 'other' | ''

/** The form's "Relationship to Subscriber" boxes; '' checks none of them. */
export type SheetRelationship = 'self' | 'spouse' | 'parent' | 'child' | 'other' | ''

export type DemographicsSheet = {
  patient: {
    fullName: string
    dateOfBirth: string
    sex: SheetSex
    preferredName: string
    preferredLanguage: string
  }
  contact: { primaryPhone: string; alternatePhone: string; email: string }
  address: { street: string; city: string; state: string; zip: string }
  insurance: {
    primaryInsurance: string
    memberId: string
    groupNumber: string
    subscriberName: string
    subscriberDateOfBirth: string
    relationship: SheetRelationship
    /** Label for a relationship outside the form's four boxes, e.g. "Injured Party". */
    relationshipOther: string
  }
  emergencyContact: { name: string; relationship: string; phone: string }
  provider: { providerName: string; npi: string; phone: string; fax: string }
  pharmacy: { name: string; phone: string; address: string }
}

function displayName(name: HumanName | undefined): string {
  return name ? formatHumanName(name) : ''
}

function phone(value: string | undefined): string {
  return value ? formatPhone(value) : ''
}

/** The legal name — never the nickname, which the form asks for separately. */
function officialName(patient: Patient): HumanName | undefined {
  const usable = patient.name?.filter((n) => n.use !== 'nickname' && n.use !== 'old')
  return usable?.find((n) => n.use === 'official') ?? usable?.[0]
}

function preferredName(patient: Patient): string {
  const nickname = patient.name?.find((n) => n.use === 'nickname')
  if (!nickname) return ''
  // A nickname is usually a bare given name; formatHumanName would render it
  // alone anyway, but fall back to its text when that is all the record has.
  return displayName(nickname) || nickname.text || ''
}

function preferredLanguage(patient: Patient): string {
  const spoken = patient.communication?.find((c) => c.preferred && c.language)?.language
  return spoken?.coding?.[0]?.display ?? spoken?.text ?? ''
}

function sexOf(patient: Patient): SheetSex {
  switch (patient.gender) {
    case 'male':
    case 'female':
    case 'other':
      return patient.gender
    default:
      // 'unknown' and a missing gender both leave every box empty rather than
      // asserting "Other" about a patient whose sex was simply never recorded.
      return ''
  }
}

/**
 * Up to two distinct phone numbers. Charts routinely carry the same number
 * twice — a duplicate telecom entry, or the same line written once as E.164 and
 * once formatted — and printing it as both "Primary" and "Alternate" reads as a
 * second contact that does not exist. Compared on digits alone for that reason.
 */
function phonesOf(patient: Patient): { primaryPhone: string; alternatePhone: string } {
  const seen = new Set<string>()
  const numbers: string[] = []
  for (const entry of patient.telecom ?? []) {
    if (entry.system !== 'phone' || entry.use === 'old' || !entry.value) continue
    const digits = entry.value.replace(/\D/g, '')
    // Drop a US country code before comparing: the doctor console writes E.164
    // (`+16462204565`) while patient-web registration writes `(646) 220-4565`,
    // so the same line reaches us in both shapes (PD-1133).
    const key = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
    if (seen.has(key)) continue
    seen.add(key)
    numbers.push(phone(entry.value))
  }
  return { primaryPhone: numbers[0] ?? '', alternatePhone: numbers[1] ?? '' }
}

function mailingAddress(patient: Patient): DemographicsSheet['address'] {
  const address = patient.address?.find((a) => a.use !== 'old')
  return {
    street: address?.line?.filter(Boolean).join(' ') ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.postalCode ?? '',
  }
}

function relationshipOf(coverage: Coverage | undefined): {
  relationship: SheetRelationship
  relationshipOther: string
} {
  const coding = coverage?.relationship?.coding?.[0]
  const code = coding?.code?.toLowerCase()
  if (!code) return { relationship: '', relationshipOther: '' }
  if (code === 'self' || code === 'spouse' || code === 'parent' || code === 'child') {
    return { relationship: code, relationshipOther: '' }
  }
  return {
    relationship: 'other',
    relationshipOther: coding?.display ?? coverage?.relationship?.text ?? '',
  }
}

function insuranceOf(
  patient: Patient,
  coverage: Coverage | undefined,
): DemographicsSheet['insurance'] {
  const { relationship, relationshipOther } = relationshipOf(coverage)
  if (!coverage) {
    return {
      primaryInsurance: '',
      memberId: '',
      groupNumber: '',
      subscriberName: '',
      subscriberDateOfBirth: '',
      relationship,
      relationshipOther,
    }
  }

  const { payorName, memberId, group } = getInsuranceDisplayData(coverage)
  // On a self coverage the subscriber IS the patient, and the subscriber
  // extensions are left unset — so read the patient rather than print blanks.
  const isSelf = relationship === 'self'
  return {
    primaryInsurance: payorName,
    memberId,
    groupNumber: group,
    subscriberName: isSelf
      ? displayName(officialName(patient))
      : (getSubscriberName(coverage) ?? ''),
    subscriberDateOfBirth: isSelf
      ? convertFromISODate(patient.birthDate)
      : convertFromISODate(getSubscriberDob(coverage)),
    relationship,
    relationshipOther,
  }
}

function emergencyContactOf(patient: Patient): DemographicsSheet['emergencyContact'] {
  // beHuman has no UI for this today; a value only exists on records imported
  // from an outside source, in which case the section prints.
  const contact = patient.contact?.find((c) => c.name || c.telecom?.length)
  const relationship = contact?.relationship?.[0]
  return {
    name: displayName(contact?.name),
    relationship: relationship?.coding?.[0]?.display ?? relationship?.text ?? '',
    phone: phone(contact?.telecom?.find((t) => t.system === 'phone')?.value),
  }
}

function providerOf(provider: SheetProviderInput | undefined): DemographicsSheet['provider'] {
  return {
    providerName: provider?.name ?? '',
    npi: provider?.npi ?? '',
    phone: phone(provider?.phone),
    fax: phone(provider?.fax),
  }
}

/**
 * The patient's default pharmacy as PD-1386 stores it on the Patient extension.
 * Read through the same helpers the Demographics card uses, so the sheet and the
 * screen can never disagree.
 */
function pharmacyOf(patient: Patient): DemographicsSheet['pharmacy'] {
  const stored = readDefaultPharmacy(patient)
  if (!stored) return { name: '', phone: '', address: '' }
  return {
    name: stored.storeName,
    phone: phone(stored.phone),
    address: formatPharmacyAddress(stored),
  }
}

export function buildDemographicsSheet(
  patient: Patient,
  primaryCoverage: Coverage | undefined,
  provider: SheetProviderInput | undefined,
): DemographicsSheet {
  return {
    patient: {
      fullName: displayName(officialName(patient)),
      dateOfBirth: convertFromISODate(patient.birthDate),
      sex: sexOf(patient),
      preferredName: preferredName(patient),
      preferredLanguage: preferredLanguage(patient),
    },
    contact: {
      ...phonesOf(patient),
      email: patient.telecom?.find((t) => t.system === 'email')?.value ?? '',
    },
    address: mailingAddress(patient),
    insurance: insuranceOf(patient, primaryCoverage),
    emergencyContact: emergencyContactOf(patient),
    provider: providerOf(provider),
    pharmacy: pharmacyOf(patient),
  }
}
